import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useLockPartnership } from '@Hooks/Permissions';
import { ApiPartnership, EnumAccessState } from '@Model';
import { ButtonWithConfirmationModal, EnumMessageLevel, EnumMessageType } from '@synqo/frontend-component-library';

interface ILockPartnershipButtonProps {
    partnership: ApiPartnership | undefined;
    refetchPartnership: () => void;
}

export function LockPartnershipButton({ partnership, refetchPartnership }: ILockPartnershipButtonProps) {
    const hasPermission = useHasPermission();

    const partnershipId = partnership?.id ?? '';
    const { lockAsync, lockPartnershipIsPending, unlockAsync, unlockPartnershipIsPending } = useLockPartnership(
        partnershipId,
        refetchPartnership,
    );

    const canLockPartnership = hasPermission(Permission.PermissionsPartnershipsLock);
    const canUnlockPartnership = hasPermission(Permission.PermissionsPartnershipsUnlock);

    if (!partnership) {
        return null;
    }

    const partnershipIsLocked = partnership.access_state === EnumAccessState.Locked;
    if (partnershipIsLocked ? !canUnlockPartnership : !canLockPartnership) {
        return null;
    }

    return partnershipIsLocked ? (
        <ButtonWithConfirmationModal
            key="unlock_partnership"
            onClick={unlockAsync}
            locked={unlockPartnershipIsPending}
            confirmModalMessage={{
                message: 'Wollen Sie den Zugang wirklich entsperren?',
                title: 'Entsperren bestätigen',
                type: EnumMessageType.MultipleChoice,
                level: EnumMessageLevel.Information,
            }}
            variant="warning"
        >
            {unlockPartnershipIsPending ? 'Wird entsperrt...' : 'Zugang entsperren'}
        </ButtonWithConfirmationModal>
    ) : (
        <ButtonWithConfirmationModal
            key="lock_partnership"
            onClick={lockAsync}
            locked={lockPartnershipIsPending}
            confirmModalMessage={{
                message: 'Wollen Sie den Zugang wirklich sperren?',
                title: 'Sperren bestätigen',
                type: EnumMessageType.MultipleChoice,
                level: EnumMessageLevel.Warning,
            }}
            variant="secondary"
        >
            {lockPartnershipIsPending ? 'Wird gesperrt...' : 'Zugang sperren'}
        </ButtonWithConfirmationModal>
    );
}
